import { computed } from "vue";
import { useThemeStore } from "@/stores/useThemeStore";

export function useTradingViewTheme() {
	const themeStore = useThemeStore();

	const tradingViewTheme = computed(() => (themeStore.isDark ? "dark" : "light"));

	/**
	 * Generates CSS styles for TradingView widgets to match site theme
	 */
	const generateTradingViewStyles = () => {
		return `
			/* Default widget styling */
			.tradingview-widget-container__widget {
				background-color: var(--background) !important;
				color: var(--text) !important;
				overflow: hidden !important;
			}

			/* Preserve ticker tape visibility and background harmony */
			.tradingview-widget-container__widget * {
				color: inherit !important;
			}

			/* Widget content areas */
			.tradingview-widget-container__widget [style*="background"],
			.tradingview-widget-container__widget [style*="background-color"] {
				background-color: var(--background) !important;
			}

			/* Match positive/negative colors to site theme */
			.tradingview-widget-container__widget [style*="rgb(34, 139, 34)"],
			.tradingview-widget-container__widget [style*="#228B22"],
			.tradingview-widget-container__widget [style*="green"],
			.tradingview-widget-container__widget [style*="color: green"] {
				color: var(--positive) !important;
			}

			.tradingview-widget-container__widget [style*="rgb(255, 0, 0)"],
			.tradingview-widget-container__widget [style*="#FF0000"],
			.tradingview-widget-container__widget [style*="red"],
			.tradingview-widget-container__widget [style*="color: red"] {
				color: var(--negative) !important;
			}

			/* Ticker tape specific - blend completely with site background */
			.tradingview-widget-container__widget iframe[src*="ticker-tape"] {
				background-color: var(--background) !important;
				border: none !important;
			}

			/* Calendar specific styling - with borders and card background */
			.tradingview-widget-container__widget iframe[src*="events"] {
				background-color: var(--card) !important;
				border: 1px solid var(--border) !important;
				border-radius: var(--radius) !important;
			}

			.tradingview-widget-container__widget table,
			.tradingview-widget-container__widget tbody,
			.tradingview-widget-container__widget thead,
			.tradingview-widget-container__widget tr,
			.tradingview-widget-container__widget td,
			.tradingview-widget-container__widget th {
				background-color: var(--card) !important;
				border-color: var(--border) !important;
				color: var(--text) !important;
			}

			/* Heatmap specific styling - with borders and card background */
			.tradingview-widget-container__widget iframe[src*="heatmap"] {
				background-color: var(--card) !important;
				border: 1px solid var(--border) !important;
				border-radius: var(--radius) !important;
			}

			.tradingview-widget-container__widget iframe[src*="stock-heatmap"],
			.tradingview-widget-container__widget iframe[src*="crypto-coins-heatmap"] {
				background-color: var(--card) !important;
				border: 1px solid var(--border) !important;
				border-radius: var(--radius) !important;
			}
		`;
	};

	/**
	 * Applies minimal styles to preserve widget functionality
	 */
	const applyStylesToContainer = (container: HTMLElement, delay = 800) => {
		// Detect if this is a ticker tape (positioned absolutely at top)
		const isTickerTape = container.closest(".absolute.top-0") !== null;

		// For ticker tape, apply styles immediately and repeatedly
		if (isTickerTape) {
			const applyTickerStyles = () => {
				// Remove any existing custom styles first
				const existingStyles = container.querySelectorAll(
					'[data-tradingview-theme="custom"]',
				);
				for (const style of existingStyles) {
					style.remove();
				}

				const style = document.createElement("style");
				style.setAttribute("data-tradingview-theme", "custom");
				style.innerHTML = `
					/* Ticker tape - aggressive background override */
					.tradingview-widget-container__widget,
					.tradingview-widget-container iframe,
					.tradingview-widget-container * {
						background-color: var(--background) !important;
						border: none !important;
						color: var(--text) !important;
					}

					/* Override all possible TradingView background styles */
					.tradingview-widget-container [style*="background"],
					.tradingview-widget-container [style*="background-color"],
					.tradingview-widget-container [style*="white"],
					.tradingview-widget-container [style*="#fff"],
					.tradingview-widget-container [style*="#ffffff"],
					.tradingview-widget-container [style*="rgb(255, 255, 255)"],
					.tradingview-widget-container div,
					.tradingview-widget-container span {
						background-color: var(--background) !important;
					}
				`;
				container.appendChild(style);
			};

			// Apply immediately and then repeatedly to catch dynamic content
			applyTickerStyles();
			setTimeout(applyTickerStyles, 200);
			setTimeout(applyTickerStyles, 500);
			setTimeout(applyTickerStyles, 1000);
			setTimeout(applyTickerStyles, 2000);
		} else {
			// Calendar - apply once with delay
			setTimeout(() => {
				const existingStyles = container.querySelectorAll(
					'[data-tradingview-theme="custom"]',
				);
				for (const style of existingStyles) {
					style.remove();
				}

				const style = document.createElement("style");
				style.setAttribute("data-tradingview-theme", "custom");
				style.innerHTML = generateTradingViewStyles();
				container.appendChild(style);
			}, delay);
		}
	};
	return {
		tradingViewTheme,
		generateTradingViewStyles,
		applyStylesToContainer,
	};
}
